import { inject, Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { DeliveryDetailsService } from '../services/delivery-details.service';
import * as OrderApproveSelectors from '../reducers/order-approve.reducer';
import { State } from '../reducers';
import * as OrderApproveActions from '../actions/order-approve.actions';
import {
  IAddressData,
  IAddressesDataIncluded,
  ICCPaymentResponse,
  ICheckoutData,
  ICheckoutUpdate,
  IConsentCheckbox,
  ICustomAddressData,
  ISipayceCardPaymentData,
  ISipayceCardPaymentResponse,
  IUploadedDocument,
  IUploadFileResponse,
} from '../models/checkout.models';
import { CustomerService, RfqService } from '../services';
import {
  ICreateRfQ,
  IRfqBaseInfo,
  IRfqCancelDetails,
  IRfqCreateResponse,
  IRfqDetails,
  IRfqHistory,
} from '../models/rfq.models';
import { OrdersService } from '../services/orders.service';
import { CheckoutActions } from '../actions/';
import * as CheckoutSelectors from '../reducers/checkout.reducer';
import { IAddress, IPointOfContact, ITotals } from '../models/common.models';
import { ICartVoucher } from '../models/cart.models';
import { IApproverData } from '../models/customer.models';
import { HttpResponse } from '@angular/common/http';
import { IBaseConfig } from '../models/enviroment-delivery-details.model';

@Injectable({
  providedIn: 'root',
})
export class CheckoutFacade {
  private readonly injector: Injector = inject(Injector);
  private readonly store: Store<State> = inject(Store<State>);

  private pCustomerService: CustomerService | undefined;
  private pDeliveryDetailsService: DeliveryDetailsService | undefined;
  private pRfqService: RfqService | undefined;
  private pOrderService: OrdersService | undefined;

  public get customerService(): CustomerService {
    if (!this.pCustomerService) {
      this.pCustomerService = this.injector.get(CustomerService);
    }
    return this.pCustomerService;
  }

  public get deliveryDetailsService(): DeliveryDetailsService {
    if (!this.pDeliveryDetailsService) {
      this.pDeliveryDetailsService = this.injector.get(DeliveryDetailsService);
    }
    return this.pDeliveryDetailsService;
  }

  public get ordersService(): OrdersService {
    if (!this.pOrderService) {
      this.pOrderService = this.injector.get(OrdersService);
    }
    return this.pOrderService;
  }

  public get rfqService(): RfqService {
    if (!this.pRfqService) {
      this.pRfqService = this.injector.get(RfqService);
    }
    return this.pRfqService;
  }

  postSipayceCardPayment(data: ISipayceCardPaymentData): Observable<ISipayceCardPaymentResponse> {
    return this.deliveryDetailsService.postSipayceCardPayment(data);
  }

  getCCAvenuePaymentData(orderId: string): Observable<ICCPaymentResponse> {
    return this.deliveryDetailsService.getCCAvenuePaymentData(orderId);
  }

  postCustomerAddresses(customerId: string, body: any): Observable<any> {
    return this.customerService.addCustomerAddress(customerId, body);
  }

  postCheckoutUpdate(data: ICheckoutUpdate, guestUserId: string = ''): Observable<any> {
    return this.deliveryDetailsService.postCheckoutUpdate(data, guestUserId);
  }

  postCheckoutData(data: ICheckoutData, guestUserId: string = ''): Observable<any> {
    return this.deliveryDetailsService.postCheckoutData(data, guestUserId);
  }

  getShipmentMethodPrice(cartId: string): Observable<any> {
    return this.deliveryDetailsService.getShipmentMethodPrice(cartId);
  }

  getCompanyUsers(): Observable<any> {
    return this.deliveryDetailsService.getCompanyUsers();
  }

  getCompanyUsersForApprove(): Observable<any> {
    return this.deliveryDetailsService.getCompanyUsersForApprove();
  }

  //TODO: Set explicit type for the first parameter and return value.
  // Two different types are used for the parameter (ICheckout and ICheckoutInput), while they have almost the same structure.
  // Merge them together and set final type for the parameter.
  postCheckout(data: any, guestUserId: string = ''): Observable<any> {
    return this.deliveryDetailsService.postCheckout(data, guestUserId);
  }

  successSummaryPageData(): Observable<any> {
    return this.store.select(OrderApproveSelectors.getOrderSuccessPageData);
  }

  successSummaryPageClearData(): void {
    this.store.dispatch(OrderApproveActions.successSummaryPageClearData());
  }

  actionPutCartIdOrderApprove(cartId: string): void {
    this.store.dispatch(OrderApproveActions.PutCartId({cartId}));
  }

  actionLoadCartItemsOrderApprove(cartId: string): void {
    this.store.dispatch(OrderApproveActions.loadCartItems({cartId}));
  }

  selectCartItemsOrderApprove(): Observable<any> {
    return this.store.select(OrderApproveSelectors.getCartItems);
  }

  selectCartItemsLoadFail(): Observable<boolean> {
    return this.store.select(OrderApproveSelectors.getCartItemsLoadFail);
  }

  convertDataToDisplayAddresses(addressToAddArray: Array<IAddressesDataIncluded> | Array<ICustomAddressData>,
                                addressesList: Array<IAddressData>): IAddressData[] {
    return this.deliveryDetailsService.getAddressesFromData(addressToAddArray, addressesList);
  }

  createRfq(quote: ICreateRfQ): Observable<IRfqCreateResponse | undefined> {
    return this.rfqService.createRequestForQuote(quote);
  }

  getQuoteHistory(): Observable<IRfqHistory> {
    return this.rfqService.getRfqHistory();
  }

  loadQuoteHistory() {
    this.store.dispatch(CheckoutActions.GetQuoteRfqHistoryStartDataAction());
  }

  selectQuoteHistory(): Observable<IRfqBaseInfo[] | undefined> {
    return this.store.select(CheckoutSelectors.selectRfqHistoryData);
  }

  selectQuoteDetails(quoteId: string): Observable<IRfqDetails> {
    return this.rfqService.getRfqDetails(quoteId);
  }

  cancelRequest(quoteId: string, quote: IRfqCancelDetails): Observable<any> {
    return this.rfqService.cancelRfq(quoteId, quote);
  }

  getResponseCheckoutData(): Observable<any> {
    return this.store.select(OrderApproveSelectors.getResponseCheckoutData);
  }

  getQuotePdfFile(quoteId: string): Observable<HttpResponse<Blob>> {
    return this.rfqService.getRfqPdfFile(quoteId);
  }

  getCartDetailPdfFile(cartId: string): Observable<HttpResponse<Blob>> {
    return this.ordersService.getCartDetailPdfFile(cartId);
  }

  getCartGenerateQuotePdfFile(cartId: string, isPreview: boolean, isDownload: boolean = false): Observable<any> {
    return this.ordersService.getCartGenerateQuotePdfFile(cartId, isPreview, isDownload);
  }

  setSapPoNumber(sapPoNumber: string): void {
    this.store.dispatch(CheckoutActions.setSapPoNumber({sapPoNumber: sapPoNumber}));
  }

  setOrderEmailCopy(orderEmailCopy: string): void {
    this.store.dispatch(CheckoutActions.setOrderEmailCopy({orderEmailCopy: orderEmailCopy}));
  }

  selectSapPoNumber(): Observable<string> {
    return this.store.select(CheckoutSelectors.getSapPoNumber);
  }

  selectOrderCopyEmail(): Observable<string | undefined> {
    return this.store.select(CheckoutSelectors.getOrderCopyEmail);
  }

  selectLoadingDeliveryDetailsAddressData(): Observable<boolean> {
    return this.store.select(CheckoutSelectors.selectLoadingDeliveryDetailsAddressData);
  }

  selectAvailableShipToAddresses(): Observable<IAddress[] | undefined> {
    return this.store.select(CheckoutSelectors.selectAvailableShipToAddresses);
  }

  selectPreselectedShipToAddress(): Observable<IAddress | undefined> {
    return this.store.select(CheckoutSelectors.selectPreselectedShipToAddress);
  }

  selectIsPreselectedShipToAddressCustom$(): Observable<boolean | undefined> {
    return this.store.select(CheckoutSelectors.selectIsPreselectedShipToAddressCustom);
  }

  selectAvailableBillToAddresses(): Observable<IAddress[] | undefined> {
    return this.store.select(CheckoutSelectors.selectAvailableBillToAddresses);
  }

  selectPreselectedBillToAddress(): Observable<IAddress | undefined> {
    return this.store.select(CheckoutSelectors.selectPreselectedBillToAddress);
  }

  selectSelectedShipmentMethod(): Observable<string | undefined> {
    return this.store.select(CheckoutSelectors.selectSelectedShipmentMethod);
  }

  selectDeliveryDetailsFormData(): Observable<IPointOfContact | undefined> {
    return this.store.select(CheckoutSelectors.selectDeliveryDetailsFormData);
  }

  selectAttentionTo(): Observable<string | undefined> {
    return this.store.select(CheckoutSelectors.selectAttentionTo);
  }

  selectAvailableShipmentMethods(): Observable<IBaseConfig[] | undefined> {
    return this.store.select(CheckoutSelectors.selectAvailableShipmentMethods);
  }

  selectAvailableShipmentMethodsLoading(): Observable<boolean> {
    return this.store.select(CheckoutSelectors.selectAvailableShipmentMethodsLoading);
  }

  selectDeliveryDetailsApproverFormData(): Observable<IApproverData | undefined> {
    return this.store.select(CheckoutSelectors.selectDeliveryDetailsApproverFormData);
  }

  selectTaxNumberFormData(): Observable<string | null> {
    return this.store.select(CheckoutSelectors.selectTaxNumberFormData);
  }

  selectTermsAndConsentsData(): Observable<IConsentCheckbox[]> {
    return this.store.select(CheckoutSelectors.selectTermsAndConsentsData);
  }

  selectTotalPrices(): Observable<ITotals | undefined> {
    return this.store.select(CheckoutSelectors.selectTotalPrices);
  }

  selectTotalPricesLoading(): Observable<boolean> {
    return this.store.select(CheckoutSelectors.selectTotalPricesLoading);
  }

  selectIsSaturdayShipmentSelected(): Observable<boolean> {
    return this.store.select(CheckoutSelectors.selectIsSaturdayShipmentSelected);
  }

  selectIsHeavyweightShipmentSetAutomatically(): Observable<boolean> {
    return this.store.select(CheckoutSelectors.selectIsHeavyweightShipmentSetAutomatically);
  }

  selectCartVouchers(): Observable<ICartVoucher[] | undefined> {
    return this.store.select(CheckoutSelectors.selectCartVouchers);
  }

  selectUploadedPoDocuments(): Observable<IUploadedDocument[]> {
    return this.store.select(CheckoutSelectors.selectUploadedPoDocuments);
  }

  selectIsAnyPoDocumentUploadedSuccessfully(): Observable<boolean> {
    return this.store.select(CheckoutSelectors.selectIsAnyPoDocumentUploadedSuccessfully);
  }

  addPOUploadDocuments(documents: IUploadedDocument[]): void {
    this.store.dispatch(CheckoutActions.addPOUploadDocuments({uploadedPoDocuments: documents}));
  }

  setPOUploadDocumentUploadingInProgress(fileName: string, uploadingInProgress: boolean): void {
    this.store.dispatch(CheckoutActions.setPOUploadDocumentUploadingInProgress({fileName, uploadingInProgress}));
  }

  setPOUploadDocumentUploadSuccess(fileName: string, uploadSuccess: boolean): void {
    this.store.dispatch(CheckoutActions.setPOUploadDocumentUploadSuccess({fileName, uploadSuccess}));
  }

  setPOUploadDocumentUploadError(fileName: string, uploadError: string | null): void {
    this.store.dispatch(CheckoutActions.setPOUploadDocumentUploadError({fileName, uploadError}));
  }

  uploadPODocument(file: File, quoteReference: string): Observable<IUploadFileResponse> {
    return this.ordersService.uploadPODocument(file, quoteReference);
  }

  clearPOUploadDocuments(): void {
    this.store.dispatch(CheckoutActions.clearPOUploadDocuments());
  }

  deleteUploadedPoDocuments(quoteReference: string): Observable<any> {
    return this.ordersService.deleteUploadedPODocuments(quoteReference);
  }

  setCheckoutFormData(data: IPointOfContact): void {
    this.store.dispatch(CheckoutActions.setDeliveryDetailsFormData({deliveryDetailsFormData: data}));
  }

  setAttentionTo(attentionTo: string): void {
    this.store.dispatch(CheckoutActions.setAttentionTo({attentionTo}));
  }

  setAvailableShipmentMethods(availableShipmentMethods: IBaseConfig[]): void {
    this.store.dispatch(CheckoutActions.setAvailableShipmentMethods({availableShipmentMethods}));
  }

  setAvailableShipmentMethodsLoading(loading: boolean): void {
    this.store.dispatch(CheckoutActions.setAvailableShipmentMethodsLoading({availableShipmentMethodsLoading: loading}));
  }

  setPreselectedShipToAddress(preselectedShipToAddress: IAddress): void {
    this.store.dispatch(CheckoutActions.setPreselectedShipToAddress({preselectedShipToAddress: preselectedShipToAddress}));
  }

  setPreselectedBillToAddress(preselectedBillToAddress: IAddress): void {
    this.store.dispatch(CheckoutActions.setPreselectedBillToAddress({preselectedBillToAddress: preselectedBillToAddress}));
  }

  addShipToAddressToAvailableShipToAddresses(customShipToAddress: IAddress): void {
    this.store.dispatch(CheckoutActions.addShipToAddressToAvailableShipToAddresses({shipToAddress: customShipToAddress}));
  }

  setDeliveryDetailsApproverFormData(data: IApproverData): void {
    this.store.dispatch(CheckoutActions.setDeliveryDetailsApproverFormData({deliveryDetailsApproverFormData: data}));
  }

  setTaxNumberFormData(data: string): void {
    this.store.dispatch(CheckoutActions.setTaxNumberFormData({taxNumber: data}));
  }

  setTermsAndConsentsData(data: IConsentCheckbox[]): void {
    this.store.dispatch(CheckoutActions.setTermsAndConsentsData({terms: data}));
  }

  setCheckoutTotalPrices(data: ITotals): void {
    this.store.dispatch(CheckoutActions.setTotalPrices({totalPrices: data}));
  }

  setCheckoutTotalPricesLoading(loading: boolean): void {
    this.store.dispatch(CheckoutActions.setTotalPricesLoading({totalPricesLoading: loading}));
  }

  setIsSaturdayShipmentSelected(isSaturdayShipmentSelected: boolean): void {
    this.store.dispatch(CheckoutActions.setIsSaturdayShipmentSelected({isSaturdayShipmentSelected}));
  }

  setIsHeavyweightShipmentSetAutomatically(isHeavyweightShipmentSetAutomatically: boolean): void {
    this.store.dispatch(CheckoutActions.setIsHeavyweightShipmentSetAutomatically({isHeavyweightShipmentSetAutomatically}));
  }

  setCartVouchers(cartVouchers: ICartVoucher[]): void {
    this.store.dispatch(CheckoutActions.setCartVouchers({cartVouchers}));
  }
}
